import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import ReactLenis, { type LenisRef } from 'lenis/react'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import Hero from '@/components/sections/hero'
import AddressSearchPanel, {
  AddressSelection
} from '@/components/sections/sales-network/AddressSearchPanel'
import AreaSearchPanel from '@/components/sections/sales-network/AreaSearchPanel'
import FiltersLineNetwork from '@/components/sections/sales-network/FiltersLineNetwork'
import FreeSearchPanel from '@/components/sections/sales-network/FreeSearchPanel'
import GeolocationErrorModal from '@/components/sections/sales-network/GeolocationErrorModal'
import {
  FlyToCoords,
  MapBounds,
  SalesNetworkMap,
  StoreList
} from '@/components/sections/sales-network/index'
import NoResultsModal from '@/components/sections/sales-network/NoResultsModal'
import SearchSwitch, {
  SearchMode
} from '@/components/sections/sales-network/SearchSwitch'
import Spacer from '@/components/sections/spacer'
import { useStore } from '@/contexts/store'
import useStableViewport from '@/hooks/use-stable-viewport'

import { WPPageData } from '@/types'
import {
  GeographicArea,
  SalesPoint,
  SalesPointCategory
} from '@/types/sales-network'

// ---- Coordinate helpers (shared with SalesNetworkMap) ----

type StoreCoordinates = { lat: number; lon: number }

const haversineDistance = (
  a: { lat: number; lng: number },
  b: { lat: number; lng: number }
): number => {
  const R = 6371
  const dLat = ((b.lat - a.lat) * Math.PI) / 180
  const dLng = ((b.lng - a.lng) * Math.PI) / 180
  const sin1 = Math.sin(dLat / 2)
  const sin2 = Math.sin(dLng / 2)
  const c =
    sin1 * sin1 +
    Math.cos((a.lat * Math.PI) / 180) *
      Math.cos((b.lat * Math.PI) / 180) *
      sin2 *
      sin2
  return 2 * R * Math.asin(Math.sqrt(c))
}

const toCoordinateNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') {
    const n = value.trim().replace(',', '.')
    if (!n) return null
    const p = Number(n)
    if (Number.isFinite(p)) return p
    const fp = Number.parseFloat(n)
    return Number.isFinite(fp) ? fp : null
  }
  return null
}

const isValidLat = (v: number) => v >= -90 && v <= 90
const isValidLon = (v: number) => v >= -180 && v <= 180

const getStoreCoordinates = (store: SalesPoint): StoreCoordinates | null => {
  const rawLat = toCoordinateNumber(store?.acf?.place?.lat)
  const rawLon = toCoordinateNumber(store?.acf?.place?.lon)
  if (rawLat === null || rawLon === null) return null
  if (isValidLat(rawLat) && isValidLon(rawLon))
    return { lat: rawLat, lon: rawLon }
  if (isValidLat(rawLon) && isValidLon(rawLat))
    return { lat: rawLon, lon: rawLat }
  return null
}

const enrichStoresWithDistance = (
  stores: SalesPoint[],
  centerPoint: { lat: number; lng: number } | null
): SalesPoint[] => {
  if (!centerPoint) {
    return stores.map(({ storeDistance: _storeDistance, ...store }) => store)
  }

  return stores.map((store) => {
    const coordinates = getStoreCoordinates(store)

    if (!coordinates) {
      const { storeDistance: _storeDistance, ...rest } = store
      return rest
    }

    const distance = haversineDistance(centerPoint, {
      lat: coordinates.lat,
      lng: coordinates.lon
    })

    return {
      ...store,
      storeDistance: `${distance.toFixed(1)} km`
    }
  })
}

const TYPOLOGY_ORDER = ['flagship', 'premium', 'store'] as const

// ---- Types ----

export type SalesNetworkProps = Partial<{
  pageProps: WPPageData & {
    acf?: { hero?: { title?: string; text?: string } }
    stores?: SalesPoint[]
    tipologie?: SalesPointCategory[]
    countries?: GeographicArea[]
  }
}>

// ---- Component ----

const SalesNetwork = ({ pageProps, ...props }: SalesNetworkProps) => {
  const { strings } = useStore()
  const { isMobile } = useStableViewport()

  const [searchMode, setSearchMode] = useState<SearchMode>('free')
  const searchModeRef = useRef<SearchMode>('free')
  searchModeRef.current = searchMode
  // true when the mode was auto-switched (not by user), so we can restore 'free' on desktop
  const autoSwitchedRef = useRef(false)

  const [userGeolocation, setUserGeolocation] = useState<{
    lat: number
    lng: number
  } | null>(null)
  const [geolocationAsked, setGeolocationAsked] = useState(false)
  const [addressSearchPoint, setAddressSearchPoint] = useState<{
    lat: number
    lng: number
  } | null>(null)

  const [selectedStore, setSelectedStore] = useState<SalesPoint | null>(null)
  const [selectedCategories, setSelectedCategories] = useState<number[]>([])
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null)
  const [flyToCoords, setFlyToCoords] = useState<FlyToCoords | null>(null)
  const [visibleStores, setVisibleStores] = useState<SalesPoint[] | null>(null)
  const [showCards, setShowCards] = useState(!isMobile)
  const [mapResetKey, setMapResetKey] = useState(0)
  const [noResultsModalOpen, setNoResultsModalOpen] = useState(false)
  const [geolocationErrorModalOpen, setGeolocationErrorModalOpen] =
    useState(false)
  const [isTypologySortEnabled, setIsTypologySortEnabled] = useState(true)

  const [selectedCountryId, setSelectedCountryId] = useState<number | null>(
    null
  )
  const [selectedRegionId, setSelectedRegionId] = useState<number | null>(null)

  // Address mode: bbox returned by Mapbox for the searched place
  const [addressBbox, setAddressBbox] = useState<
    [number, number, number, number] | null
  >(null)

  const storeListLenisRef = useRef<LenisRef>(null)
  const mapInitialLoadRef = useRef(false)

  const storeData = useMemo(() => pageProps?.stores ?? [], [pageProps?.stores])
  const typologies = useMemo(
    () => pageProps?.tipologie ?? [],
    [pageProps?.tipologie]
  )

  // ---- Country/region terms derived from store taxonomy ----

  const allCountryTerms = useMemo(() => {
    const seen = new Map<number, SalesPointCategory>()
    for (const store of storeData)
      for (const term of store.taxonomies?.['store-country'] ?? [])
        if (!seen.has(term.term_id)) seen.set(term.term_id, term)
    return Array.from(seen.values())
  }, [storeData])

  const countryTerms = useMemo(
    () =>
      allCountryTerms
        .filter((t) => t.parent === 0)
        .sort((a, b) => a.name.localeCompare(b.name, 'it')),
    [allCountryTerms]
  )

  const regionTerms = useMemo(
    () =>
      selectedCountryId
        ? allCountryTerms
            .filter((t) => t.parent === selectedCountryId)
            .sort((a, b) => a.name.localeCompare(b.name, 'it'))
        : [],
    [allCountryTerms, selectedCountryId]
  )

  // ---- Geolocation ----

  const requestGeolocation = useCallback((userInitiated = false) => {
    if (!navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const point = { lat: pos.coords.latitude, lng: pos.coords.longitude }
        console.log('Geolocation obtained:', point)
        setUserGeolocation(point)
        setGeolocationAsked(true)
        if (searchModeRef.current === 'address') setAddressSearchPoint(point)
      },
      () => {
        setGeolocationAsked(true)
        if (userInitiated) setGeolocationErrorModalOpen(true)
      }
    )
  }, [])

  // Ask once on mount
  useEffect(() => {
    if (!geolocationAsked) requestGeolocation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fly to user position when it becomes available
  useEffect(() => {
    if (!userGeolocation) return
    setMapBounds(null)
    setFlyToCoords({
      lng: userGeolocation.lng,
      lat: userGeolocation.lat,
      zoom: 10
    })
  }, [userGeolocation])

  // Auto-switch: 'free' → 'address' on mobile, restore 'free' on desktop if never user-set
  useEffect(() => {
    if (isMobile && searchMode === 'free') {
      setSearchMode('address')
      autoSwitchedRef.current = false
    } else if (!isMobile && autoSwitchedRef.current) {
      setSearchMode('free')
      autoSwitchedRef.current = false
    }
  }, [isMobile, searchMode])

  // Default toggle state: always group by typology
  useEffect(() => {
    setIsTypologySortEnabled(true)
  }, [isMobile])

  // Show the store list immediately on desktop. `useStableViewport` reports
  // `isMobile: true` during SSR/initial mount, so the useState initializer
  // can't pick up the real viewport — sync it here after hydration.
  useEffect(() => {
    if (!isMobile) setShowCards(true)
  }, [isMobile])

  // ---- Handlers ----

  const handleSearchModeChange = useCallback(
    (mode: SearchMode) => {
      autoSwitchedRef.current = false
      setSearchMode(mode)
      setSelectedStore(null)
      setSelectedCategories([])
      setAddressBbox(null)
      setAddressSearchPoint(null)
      setSelectedCountryId(null)
      setSelectedRegionId(null)
      setMapBounds(null)
      setFlyToCoords(null)
      setShowCards(!isMobile)
      setVisibleStores(null)
      mapInitialLoadRef.current = false
      setMapResetKey((k) => k + 1)
      if (!geolocationAsked) requestGeolocation()
    },
    [geolocationAsked, requestGeolocation, isMobile]
  )

  const handleCategoriesSelect = (categoryId: number) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    )
    setSelectedStore(null)
    if (!isMobile) setShowCards(true)
  }

  const handleAddressSelect = useCallback(({ coords }: AddressSelection) => {
    setShowCards(true)
    setSelectedStore(null)
    setAddressSearchPoint({ lat: coords.lat, lng: coords.lng })
    setAddressBbox(coords.bbox ?? null)
    setMapBounds(null)
    setFlyToCoords(coords)
  }, [])

  const handleAddressReset = useCallback(() => {
    setAddressBbox(null)
    setAddressSearchPoint(null)
    setFlyToCoords(null)
    setMapBounds(null)
    setSelectedStore(null)
  }, [])

  const handleCountrySelect = useCallback(
    (countryId: number | null) => {
      setSelectedCountryId(countryId)
      setSelectedRegionId(null)
      setSelectedStore(null)
      setShowCards(true)
      if (countryId) {
        const area = pageProps?.countries?.find((c) => c.term_id === countryId)
        if (area) {
          setFlyToCoords(null)
          setMapBounds(area.acf)
        }
      } else {
        setMapBounds(null)
      }
    },
    [pageProps?.countries]
  )

  const handleRegionSelect = useCallback(
    (regionId: number | null) => {
      setSelectedRegionId(regionId)
      setSelectedStore(null)
      setShowCards(true)
      if (!regionId) return

      const coords = storeData
        .filter((s) =>
          s.taxonomies?.['store-country']?.some((t) => t.term_id === regionId)
        )
        .map(getStoreCoordinates)
        .filter((c): c is StoreCoordinates => c !== null)

      if (coords.length === 0) return
      const lats = coords.map((c) => c.lat)
      const lons = coords.map((c) => c.lon)
      setFlyToCoords(null)
      setMapBounds({
        topleft: { lat: Math.max(...lats), lon: Math.min(...lons) },
        bottomright: { lat: Math.min(...lats), lon: Math.max(...lons) }
      })
    },
    [storeData]
  )

  const scrollToStoreCard = useCallback((storeId: string | number) => {
    const lenis = storeListLenisRef.current?.lenis
    if (!lenis) return
    const card = storeListLenisRef.current?.wrapper?.querySelector(
      `[data-store-id="${storeId}"]`
    ) as HTMLElement | null
    if (card) lenis.scrollTo(card, { offset: 0, duration: 0.6 })
  }, [])

  const handleStoreSelect = (store: SalesPoint) => {
    setSelectedStore(store)
    scrollToStoreCard(store.id)
  }

  const handleCardStoreSelect = (store: SalesPoint) => {
    setSelectedStore(store)
    const coords = getStoreCoordinates(store)
    if (!coords) {
      setFlyToCoords(null)
      return
    }
    setMapBounds(null)
    setFlyToCoords({ lng: coords.lon, lat: coords.lat, zoom: 14 })
  }

  const handleVisibleStoresChange = useCallback((stores: SalesPoint[]) => {
    setVisibleStores(stores)
    if (!mapInitialLoadRef.current) {
      mapInitialLoadRef.current = true
    } else {
      setShowCards(true)
    }
  }, [])

  // ---- Derived store sets ----

  // Stores filtered by mode (area/viewport) but NOT by typology category.
  // Used as base for counts and passed to the map.
  const modeFilteredStores = useMemo(() => {
    if (searchMode === 'area') {
      if (selectedRegionId) {
        return storeData.filter((s) =>
          s.taxonomies?.['store-country']?.some(
            (t) => t.term_id === selectedRegionId
          )
        )
      }
      if (selectedCountryId) {
        const regionIds = allCountryTerms
          .filter((t) => t.parent === selectedCountryId)
          .map((t) => t.term_id)
        const ids = new Set([selectedCountryId, ...regionIds])
        return storeData.filter((s) =>
          s.taxonomies?.['store-country']?.some((t) => ids.has(t.term_id))
        )
      }
      return storeData
    }

    if (searchMode === 'address') {
      if (addressBbox) {
        const [minLng, minLat, maxLng, maxLat] = addressBbox
        return storeData.filter((s) => {
          const c = getStoreCoordinates(s)
          return (
            c !== null &&
            c.lon >= minLng &&
            c.lon <= maxLng &&
            c.lat >= minLat &&
            c.lat <= maxLat
          )
        })
      }
      // No bbox (specific street address or geolocation) — use viewport on desktop
      if (!isMobile && visibleStores !== null) {
        const ids = new Set(visibleStores.map((s) => s.id))
        return storeData.filter((s) => ids.has(s.id))
      }
      return storeData
    }

    // free mode: viewport on desktop
    if (!isMobile && visibleStores !== null) {
      const ids = new Set(visibleStores.map((s) => s.id))
      return storeData.filter((s) => ids.has(s.id))
    }

    return storeData
  }, [
    storeData,
    searchMode,
    selectedRegionId,
    selectedCountryId,
    allCountryTerms,
    addressBbox,
    isMobile,
    visibleStores
  ])

  // Per-typology counts derived from mode-filtered stores (ignores active category filter).
  const typologyCounts = useMemo(() => {
    const counts: Record<number, number> = {}
    for (const store of modeFilteredStores)
      for (const cat of store.taxonomies?.['store-typology'] ?? [])
        counts[cat.term_id] = (counts[cat.term_id] ?? 0) + 1
    return counts
  }, [modeFilteredStores])

  // Final list: mode-filtered + category filter applied (OR: store matches any active filter).
  const displayedStores = useMemo(() => {
    if (selectedCategories.length === 0) return modeFilteredStores
    return modeFilteredStores.filter((s) =>
      s.taxonomies?.['store-typology']?.some((cat) =>
        selectedCategories.includes(cat.term_id)
      )
    )
  }, [modeFilteredStores, selectedCategories])

  // In address mode: when enabled group by typology (flagship → premium → store),
  // otherwise sort only by proximity.
  const sortedDisplayedStores = useMemo(() => {
    const compareByTitle = (a: SalesPoint, b: SalesPoint) =>
      a.title.rendered.localeCompare(b.title.rendered, 'it', {
        sensitivity: 'base'
      })

    // Default (free / area / address without a reference point): group by
    // typology (flagship → premium → store) and sort A→Z by name within each
    // group. Stores without a matching typology fall in an "other" bucket
    // appended at the end, also sorted A→Z.
    if (searchMode !== 'address' || !addressSearchPoint) {
      const groups = new Map<string, SalesPoint[]>(
        TYPOLOGY_ORDER.map((slug) => [slug, []])
      )
      const ungrouped: SalesPoint[] = []

      for (const store of displayedStores) {
        const typologies = store.taxonomies?.['store-typology'] ?? []
        const slug = TYPOLOGY_ORDER.find((g) =>
          typologies.some((t) => t.slug === g)
        )
        slug ? groups.get(slug)!.push(store) : ungrouped.push(store)
      }

      const result: SalesPoint[] = []
      for (const slug of TYPOLOGY_ORDER) {
        result.push(...(groups.get(slug) ?? []).sort(compareByTitle))
      }
      result.push(...ungrouped.sort(compareByTitle))
      return result
    }

    const withDist = displayedStores.map((s) => {
      const c = getStoreCoordinates(s)
      return {
        store: s,
        dist: c
          ? haversineDistance(addressSearchPoint, { lat: c.lat, lng: c.lon })
          : Infinity
      }
    })

    if (!isTypologySortEnabled) {
      return withDist.sort((a, b) => a.dist - b.dist).map((x) => x.store)
    }

    const groups = new Map<string, typeof withDist>(
      TYPOLOGY_ORDER.map((slug) => [slug, []])
    )
    const ungrouped: typeof withDist = []

    for (const item of withDist) {
      const typologies = item.store.taxonomies?.['store-typology'] ?? []
      const slug = TYPOLOGY_ORDER.find((g) =>
        typologies.some((t) => t.slug === g)
      )
      slug ? groups.get(slug)!.push(item) : ungrouped.push(item)
    }

    const result: SalesPoint[] = []
    for (const slug of TYPOLOGY_ORDER) {
      result.push(
        ...(groups.get(slug) ?? [])
          .sort((a, b) => a.dist - b.dist)
          .map((x) => x.store)
      )
    }
    result.push(
      ...ungrouped.sort((a, b) => a.dist - b.dist).map((x) => x.store)
    )

    return result
  }, [displayedStores, searchMode, addressSearchPoint, isTypologySortEnabled])

  const mapCenterPoint = useMemo(() => {
    if (searchMode === 'address') return addressSearchPoint
    if (searchMode === 'free') return userGeolocation
    return null
  }, [searchMode, addressSearchPoint, userGeolocation])

  const storesWithDistance = useMemo(
    () => enrichStoresWithDistance(sortedDisplayedStores, mapCenterPoint),
    [sortedDisplayedStores, mapCenterPoint]
  )

  // Refresh ScrollTrigger when list height changes on mobile
  useEffect(() => {
    if (!isMobile) return
    const raf = requestAnimationFrame(() => ScrollTrigger.refresh())
    return () => cancelAnimationFrame(raf)
  }, [isMobile, displayedStores])

  // Open no-results modal in address mode when search yields zero stores
  useEffect(() => {
    if (
      searchMode === 'address' &&
      addressSearchPoint !== null &&
      sortedDisplayedStores.length === 0
    ) {
      setNoResultsModalOpen(true)
    } else {
      setNoResultsModalOpen(false)
    }
  }, [searchMode, addressSearchPoint, sortedDisplayedStores.length])

  // ---- Render ----

  return (
    <Section {...props}>
      <Hero
        type="sales-network"
        title={pageProps?.acf?.hero?.title}
        text={pageProps?.acf?.hero?.text}
      />

      <Spacer variant="md" />

      <Container>
        <SearchSwitch
          mode={searchMode}
          onChange={handleSearchModeChange}
          isMobile={isMobile}
          className="inline-flex"
        />
      </Container>

      <Spacer variant="md" />

      <Container>
        {searchMode === 'free' && (
          <FreeSearchPanel
            onUseCurrentLocation={() => requestGeolocation(true)}
          />
        )}
        {searchMode === 'address' && (
          <AddressSearchPanel
            onAddressSelect={handleAddressSelect}
            onAddressReset={handleAddressReset}
            onUseCurrentLocation={() => requestGeolocation(true)}
            typologySortEnabled={isTypologySortEnabled}
            onTypologySortChange={setIsTypologySortEnabled}
            userGeolocation={userGeolocation}
          />
        )}
        {searchMode === 'area' && (
          <AreaSearchPanel
            countryTerms={countryTerms}
            regionTerms={regionTerms}
            selectedCountryId={selectedCountryId}
            selectedRegionId={selectedRegionId}
            onCountrySelect={handleCountrySelect}
            onRegionSelect={handleRegionSelect}
            onUseCurrentLocation={() => requestGeolocation(true)}
          />
        )}
      </Container>

      <Spacer variant="lg" />

      <Container>
        <Grid className="gap-y-spacing-md">
          {/* Sidebar: filters + store list */}
          <div className="order-2 col-span-2 md:col-span-6 lg:order-1 lg:col-span-4">
            <div className="sticky top-header-height flex flex-col">
              <Typography
                variant="h5"
                component="h2"
                className="text-text-heading-dark dark:text-text-heading-dark"
              >
                {strings?.labels?.sales_network_results}
              </Typography>

              <hr className="border-0 pt-spacing-sm" />

              {typologies.length > 0 && (
                <>
                  <FiltersLineNetwork
                    categories={typologies}
                    selectedCategories={selectedCategories}
                    counts={typologyCounts}
                    onCategorySelect={handleCategoriesSelect}
                  />
                  <hr className="border-0 pt-spacing-xl" />
                </>
              )}

              {isMobile ? (
                <StoreList
                  stores={storesWithDistance}
                  categories={typologies}
                  selectedStore={selectedStore}
                  onStoreSelect={handleCardStoreSelect}
                  emptyMessage={strings?.labels?.sales_network_no_results}
                />
              ) : showCards ? (
                <ReactLenis
                  ref={storeListLenisRef}
                  className="max-h-[60vh] overflow-hidden pr-spacing-2xs"
                  options={{ smoothWheel: true }}
                  data-lenis-prevent
                >
                  <StoreList
                    stores={storesWithDistance}
                    categories={typologies}
                    selectedStore={selectedStore}
                    onStoreSelect={handleCardStoreSelect}
                    emptyMessage={strings?.labels?.sales_network_no_results}
                  />
                </ReactLenis>
              ) : null}
            </div>
          </div>

          {/* Map (desktop only) */}
          {!isMobile && (
            <div className="order-1 col-span-2 md:col-span-6 lg:order-2 lg:col-span-8 lg:-mr-24 xl:-mr-40">
              <div className="h-[calc(var(--vh)*50)] lg:sticky lg:top-spacing-lg lg:h-[calc(var(--vh)*100)]">
                <SalesNetworkMap
                  stores={displayedStores}
                  allStores={storeData}
                  categories={typologies}
                  selectedStore={selectedStore}
                  onStoreSelect={handleStoreSelect}
                  onVisibleStoresChange={handleVisibleStoresChange}
                  bounds={mapBounds}
                  flyToCoords={flyToCoords}
                  resetKey={mapResetKey}
                  searchPin={
                    searchMode === 'address'
                      ? addressSearchPoint
                      : userGeolocation
                  }
                />
              </div>
            </div>
          )}
        </Grid>
      </Container>

      <Spacer variant="xl" />

      <NoResultsModal
        open={noResultsModalOpen}
        onClose={() => setNoResultsModalOpen(false)}
      />

      <GeolocationErrorModal
        open={geolocationErrorModalOpen}
        onClose={() => setGeolocationErrorModalOpen(false)}
      />
    </Section>
  )
}

export default SalesNetwork
